import type { FC, ReactNode } from 'react'
import type { ConsortiumCategory } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getStartingInstallment } from '@/lib/consortium'
import { formatCompactCurrency, formatCurrency } from '@/lib/format'
import { ConsortiumIcon } from '@/components/landing/consortium-icon'

type CardTone = 'ink' | 'gold' | 'plain'

interface ConsortiumCardProps {
    category: ConsortiumCategory
    tone?: CardTone
    size?: 'large' | 'regular'
    className?: string
}

const toneStyles: Record<CardTone, { card: string, muted: string, tile: string, line: string, arrow: string }> = {
    ink: {
        card: 'bg-ink text-primary-foreground hover:shadow-2xl hover:shadow-ink/30',
        muted: 'text-primary-foreground/70',
        tile: 'bg-gold text-gold-foreground',
        line: 'border-ink-line',
        arrow: 'border-ink-line group-hover:border-gold group-hover:bg-gold group-hover:text-gold-foreground'
    },
    gold: {
        card: 'bg-gold text-gold-foreground hover:shadow-2xl hover:shadow-gold/40',
        muted: 'text-gold-foreground/75',
        tile: 'bg-ink text-gold',
        line: 'border-gold-foreground/15',
        arrow: 'border-gold-foreground/25 group-hover:border-ink group-hover:bg-ink group-hover:text-gold'
    },
    plain: {
        card: 'bg-card text-card-foreground ring-1 ring-border hover:shadow-xl hover:shadow-ink/10',
        muted: 'text-muted-foreground',
        tile: 'bg-sand text-bronze',
        line: 'border-border',
        arrow: 'border-border group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground'
    }
}

export const ConsortiumCard: FC<ConsortiumCardProps> = ({ category, tone = 'plain', size = 'regular', className }): ReactNode => {
    const styles = toneStyles[tone]
    const isLarge = size === 'large'

    return (
        <article
            className={cn(
                'group relative isolate flex h-full flex-col overflow-hidden rounded-[1.75rem] p-7 transition-all duration-300 motion-safe:hover:-translate-y-1 has-[a:focus-visible]:outline-2 has-[a:focus-visible]:outline-offset-4 has-[a:focus-visible]:outline-ring sm:p-8',
                styles.card,
                className
            )}
        >
            <ConsortiumIcon
                slug={category.slug}
                className='pointer-events-none absolute -bottom-10 -right-10 -z-10 size-48 opacity-[0.07] transition-transform duration-500 motion-safe:group-hover:scale-110'
            />

            <div className='flex items-start justify-between gap-4'>
                <span className={cn('grid size-12 place-items-center rounded-2xl', styles.tile)}>
                    <ConsortiumIcon slug={category.slug} className='size-6' />
                </span>

                <span
                    aria-hidden='true'
                    className={cn('grid size-10 place-items-center rounded-full border transition-colors duration-300', styles.arrow)}
                >
                    <ArrowUpRight className='size-5' />
                </span>
            </div>

            <h3
                className={cn(
                    'mt-10 font-heading leading-none tracking-tight',
                    isLarge ? 'text-[2.6rem] sm:text-5xl' : 'text-[2.15rem]'
                )}
            >
                <Link
                    href={`/consorcio/${category.slug}`}
                    className='outline-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit]'
                >
                    {category.title}
                </Link>
            </h3>

            <p className={cn('mt-3 max-w-sm leading-relaxed', styles.muted)}>
                {category.tagline}
            </p>

            <div className={cn('flex-1', isLarge ? 'min-h-12' : 'min-h-10')} />

            <dl className={cn('grid grid-cols-2 gap-4 border-t pt-6', styles.line)}>
                <div>
                    <dt className={cn('text-xs font-semibold uppercase tracking-[0.14em]', styles.muted)}>
                        Créditos
                    </dt>

                    <dd className='mt-1 font-semibold'>
                        {formatCompactCurrency(category.credit.min)} a {formatCompactCurrency(category.credit.max)}
                    </dd>
                </div>

                <div>
                    <dt className={cn('text-xs font-semibold uppercase tracking-[0.14em]', styles.muted)}>
                        Parcelas desde
                    </dt>

                    <dd className='mt-1 font-semibold tabular-nums'>
                        {formatCurrency(getStartingInstallment(category))}/mês
                    </dd>
                </div>
            </dl>
        </article>
    )
}
